import type { Intake, QuestionId } from "./intake"
import type { ClockOverride } from "./workspace"

/**
 * Federal notification obligations that MAY apply, with dates computed from the user's own
 * timestamps. Source of truth: docs/research/federal-rules.json (read from primary sources and
 * verified 2026-11-03). Every entry still needs counsel review before this is presented as final
 * (DEADLINES_REVIEWED). We never state penalty amounts, only that penalties may apply.
 *
 * Conservative by design: where the legal clock starts at a later event than the user's
 * "first noticed" time (e.g. a determination), we still show the date counted from first
 * noticed as the earliest it could possibly fall, and say what the real trigger is.
 */
export const DEADLINES_REVIEWED = false
export const DEADLINES_VERIFIED_ON = "2026-11-03"

export type Anchor = "discovery" | "determination" | "awareness" | "materiality" | "reasonable-belief" | "ransom-payment"
export type Unit = "hours" | "days" | "business-days"

export interface Calc { amount: number; unit: Unit; anchor: Anchor }

export interface Obligation {
  id: string
  regime: string
  citation: string
  whoItAppliesTo: string
  notify: string
  /** Plain-language deadline exactly as researched. */
  deadline: string
  /** Plain-language description of when the legal clock actually starts. */
  clockStarts: string
  /** Present only for simple calendar math. */
  calc?: Calc
  /** Extra thresholds / conditions worth knowing. */
  conditions?: string
  /**
   * "data-acquisition": a data-breach clock. It does not start until we know someone got in without
   * permission and whether information was taken (the user can override with a counsel-set start).
   */
  gate?: "data-acquisition"
  status: "in-force" | "not-in-force" | "split"
  statusNote: string
  sourceUrl: string
  appliesWhen: (i: Intake) => boolean
}

const hipaaOrg = (i: Intake) => i.orgTypes.includes("hipaa-ce") || i.orgTypes.includes("hipaa-ba")
const phi = (i: Intake) => i.dataTypes.includes("phi")

export const OBLIGATIONS: Obligation[] = [
  {
    id: "hipaa",
    regime: "HIPAA Breach Notification Rule",
    citation: "45 CFR 164.400–414",
    whoItAppliesTo: "HIPAA covered entities; business associates notify the covered entity.",
    notify: "Affected individuals; the media if more than 500 residents of a state are affected; HHS (immediately for 500 or more individuals, otherwise annually); a business associate notifies the covered entity.",
    deadline: "Without unreasonable delay and no later than 60 calendar days. This is an outer limit, not a target. HHS notice for 500 or more is due at the same time as individual notice.",
    clockStarts: "Discovery: the first day the breach is known, or by reasonable diligence would have been known, to the covered entity (knowledge of any workforce member counts).",
    calc: { amount: 60, unit: "days", anchor: "discovery" },
    conditions: "Applies to unsecured protected health information. A documented risk assessment can show low probability of compromise. Encryption and law-enforcement delay provisions exist; ask counsel.",
    gate: "data-acquisition",
    status: "in-force",
    statusNote: "Final and in force. Proposed changes to the HIPAA Security Rule are not final.",
    sourceUrl: "https://www.ecfr.gov/current/title-45/subtitle-A/subchapter-C/part-164/subpart-D",
    appliesWhen: (i) => phi(i) && hipaaOrg(i),
  },
  {
    id: "ftc-hbnr",
    regime: "FTC Health Breach Notification Rule",
    citation: "16 CFR Part 318",
    whoItAppliesTo: "Non-HIPAA vendors of personal health records, related entities, and their service providers (including many health and fitness apps).",
    notify: "Affected individuals; the FTC (immediately for 500 or more, otherwise annually); the media if 500 or more residents of a state are affected.",
    deadline: "Without unreasonable delay and no later than 60 calendar days. FTC notice for 500 or more is due at the same time as individual notice.",
    clockStarts: "Discovery: the first day the breach is known or reasonably should have been known to the entity or any employee, officer or agent.",
    calc: { amount: 60, unit: "days", anchor: "discovery" },
    gate: "data-acquisition",
    status: "in-force",
    statusNote: "Final and in force.",
    sourceUrl: "https://www.ecfr.gov/current/title-16/chapter-I/subchapter-C/part-318",
    appliesWhen: (i) => phi(i) && !hipaaOrg(i),
  },
  {
    id: "ftc-safeguards",
    regime: "FTC Safeguards Rule: notice to the FTC",
    citation: "16 CFR 314.4(j)",
    whoItAppliesTo: "Non-bank financial institutions under FTC jurisdiction (for example mortgage lenders and brokers, finance companies, tax preparers, collection agencies).",
    notify: "The FTC, electronically. This rule does not itself require notifying individuals.",
    deadline: "As soon as possible and no later than 30 days after discovery.",
    clockStarts: "The first day the event is known to the institution (knowledge of any employee, officer or agent counts).",
    calc: { amount: 30, unit: "days", anchor: "discovery" },
    conditions: "Applies where the unauthorized acquisition of unencrypted customer information involves at least 500 consumers.",
    gate: "data-acquisition",
    status: "in-force",
    statusNote: "Final and in force.",
    sourceUrl: "https://www.ecfr.gov/current/title-16/part-314/section-314.4",
    appliesWhen: (i) => i.orgTypes.includes("financial"),
  },
  {
    id: "bank-36h",
    regime: "Bank computer-security incident notification (OCC, Federal Reserve, FDIC)",
    citation: "12 CFR Part 53 and parallel rules",
    whoItAppliesTo: "Banking organizations supervised by the OCC, Federal Reserve or FDIC, and their bank service providers.",
    notify: "Your primary federal regulator. A bank service provider notifies each affected bank customer.",
    deadline: "As soon as possible and no later than 36 hours after the bank determines a notification incident has occurred.",
    clockStarts: "When the bank determines that a 'notification incident' has occurred. That is a materiality-type standard, not simply discovery.",
    calc: { amount: 36, unit: "hours", anchor: "determination" },
    status: "in-force",
    statusNote: "Final and in force.",
    sourceUrl: "https://www.ecfr.gov/current/title-12/chapter-I/part-53",
    appliesWhen: (i) => i.orgTypes.includes("financial"),
  },
  {
    id: "ncua-72h",
    regime: "NCUA cyber incident reporting",
    citation: "12 CFR 748.1",
    whoItAppliesTo: "Federally insured credit unions.",
    notify: "NCUA, through its online Cyber Incident Report, voicemail line, or email address published on its website.",
    deadline: "As soon as possible and no later than 72 hours.",
    clockStarts: "When the credit union reasonably believes it has experienced a reportable cyber incident, or when a third party notifies it, whichever is sooner.",
    calc: { amount: 72, unit: "hours", anchor: "reasonable-belief" },
    status: "in-force",
    statusNote: "Final and in force.",
    sourceUrl: "https://www.ecfr.gov/current/title-12/chapter-VII/subchapter-A/part-748/section-748.1",
    appliesWhen: (i) => i.orgTypes.includes("financial"),
  },
  {
    id: "sec-regsp",
    regime: "SEC Regulation S-P (customer notice)",
    citation: "17 CFR 248.30",
    whoItAppliesTo: "Broker-dealers, investment companies, SEC-registered investment advisers and transfer agents.",
    notify: "Each affected individual in writing. This section does not require a filing with the SEC. A service provider must notify the covered institution.",
    deadline: "As soon as practicable and no later than 30 days after becoming aware. A service provider must notify the covered institution as soon as possible and no later than 72 hours.",
    clockStarts: "When the institution becomes aware that unauthorized access or use has occurred or is reasonably likely to have occurred.",
    calc: { amount: 30, unit: "days", anchor: "awareness" },
    conditions: "Unless a reasonable investigation shows no reasonably likely substantial harm or inconvenience. The compliance dates have passed.",
    gate: "data-acquisition",
    status: "in-force",
    statusNote: "Final and in force. The SEC's compliance dates have passed.",
    sourceUrl: "https://www.ecfr.gov/current/title-17/chapter-II/part-248/subpart-A/section-248.30",
    appliesWhen: (i) => i.orgTypes.includes("financial"),
  },
  {
    id: "sec-8k",
    regime: "SEC Form 8-K, Item 1.05 (public companies)",
    citation: "SEC Release 33-11216",
    whoItAppliesTo: "SEC registrants (public companies).",
    notify: "The public and the SEC, by filing Form 8-K.",
    deadline: "Within four business days after the company determines the incident is material.",
    clockStarts: "The materiality determination, which must be made without unreasonable delay after discovery.",
    conditions: "Applies to incidents the company determines to be material. Counsel and your disclosure team make that call.",
    status: "in-force",
    statusNote: "Final and in force. The SEC is reviewing parts of Regulation S-K but we found no proposal to change this item.",
    sourceUrl: "https://www.sec.gov/files/rules/final/2023/33-11216.pdf",
    appliesWhen: (i) => i.orgTypes.includes("public-company"),
  },
  {
    id: "dfars-72h",
    regime: "DFARS 252.204-7012 (defense contractors)",
    citation: "DFARS 252.204-7012",
    whoItAppliesTo: "Defense contractors and subcontractors whose contract includes the clause and who handle covered defense information.",
    notify: "The Department of Defense through its DIBNet portal (a DoD-approved certificate is required). Subcontractors give the DoD report number to the prime contractor.",
    deadline: "Rapidly report: within 72 hours of discovery.",
    clockStarts: "Discovery of the cyber incident.",
    calc: { amount: 72, unit: "hours", anchor: "discovery" },
    status: "in-force",
    statusNote: "In force where the clause is in your contract.",
    sourceUrl: "https://www.acquisition.gov/dfars/252.204-7012-safeguarding-covered-defense-information-and-cyber-incident-reporting",
    appliesWhen: (i) => i.orgTypes.includes("federal-contractor"),
  },
  {
    id: "circia",
    regime: "CIRCIA: CISA critical-infrastructure reporting",
    citation: "6 U.S.C. 681b",
    whoItAppliesTo: "Covered entities in 16 critical-infrastructure sectors. Scope will be set by the final rule.",
    notify: "CISA.",
    deadline: "NOT YET IN FORCE. The statute sets 72 hours for a covered cyber incident and 24 hours for a ransom payment, but those requirements do not bind anyone until a final rule takes effect.",
    clockStarts: "Under the statute: after the entity reasonably believes a covered cyber incident occurred, or after a ransom payment is made.",
    status: "not-in-force",
    statusNote: "Proposed. As of 2026-11-03 the final rule was under White House review and not published. Check again. Voluntary reporting to CISA is encouraged now.",
    sourceUrl: "https://www.cisa.gov/topics/cyber-threats-and-advisories/information-sharing/cyber-incident-reporting-critical-infrastructure-act-2022-circia",
    appliesWhen: (i) => i.orgTypes.includes("critical-infrastructure"),
  },
  {
    id: "fcc",
    regime: "FCC telecom breach rules (CPNI)",
    citation: "47 CFR 64.2011",
    whoItAppliesTo: "Telecommunications carriers and TRS providers.",
    notify: "Currently codified: the U.S. Secret Service and the FBI, then customers. Adopted amendments add the FCC and cover inadvertent exposure.",
    deadline: "Currently codified: notify the Secret Service and FBI as soon as practicable and no later than 7 business days, and wait before telling customers. Adopted amendments shorten customer notice to no later than 30 days.",
    clockStarts: "Reasonable determination that a breach occurred.",
    calc: { amount: 7, unit: "business-days", anchor: "determination" },
    status: "split",
    statusNote: "The FCC's 2023 amendments were upheld by a court in 2025 but are 'delayed indefinitely' pending an effective-date notice. The older text is still the codified rule. Ask counsel which applies.",
    sourceUrl: "https://www.ecfr.gov/current/title-47/chapter-I/subchapter-B/part-64/subpart-U/section-64.2011",
    appliesWhen: (i) => i.orgTypes.includes("telecom"),
  },
]

export function applicableObligations(i: Intake): Obligation[] {
  return OBLIGATIONS.filter((o) => o.appliesWhen(i))
}

const HOUR = 3_600_000

/** Adds business days (Mon–Fri), skipping weekends only. Federal holidays are NOT excluded, so
 *  the result can be earlier than the legal date, which is the conservative direction. */
export function addBusinessDays(start: Date, days: number): Date {
  const d = new Date(start.getTime())
  let left = days
  while (left > 0) {
    d.setDate(d.getDate() + 1)
    const wd = d.getDay()
    if (wd !== 0 && wd !== 6) left--
  }
  return d
}

export function addCalc(start: Date, c: Calc): Date {
  if (c.unit === "hours") return new Date(start.getTime() + c.amount * HOUR)
  if (c.unit === "days") {
    const d = new Date(start.getTime())
    d.setDate(d.getDate() + c.amount)
    return d
  }
  return addBusinessDays(start, c.amount)
}

export interface DueDate {
  /** Conservative date, counted from the earliest timestamp we have. */
  date: Date | null
  /** Which user timestamp it was counted from. */
  basis: "confirmed" | "noticed" | "none"
  /** True when the legal clock starts later than the timestamp we used. */
  earlierThanLegalClock: boolean
  note: string
}

/**
 * Compute a conservative due date. We count from the earliest of the user's timestamps for
 * discovery-type clocks, and from the confirmed/declared time for determination-type clocks when
 * it exists; if only first-noticed exists for a determination clock we count from that and flag
 * that the real clock starts later (so the shown date is the earliest it could fall).
 */
export function dueDate(o: Obligation, noticedIso: string, confirmedIso: string): DueDate {
  if (!o.calc) return { date: null, basis: "none", earlierThanLegalClock: false, note: "This deadline depends on a decision (for example materiality) that we can't compute for you." }
  const noticed = noticedIso ? new Date(noticedIso) : null
  const confirmed = confirmedIso ? new Date(confirmedIso) : null
  const valid = (d: Date | null) => !!d && !Number.isNaN(d.getTime())
  const discoveryLike = o.calc.anchor === "discovery" || o.calc.anchor === "awareness"
  const start = discoveryLike ? (valid(noticed) ? noticed : null) : valid(confirmed) ? confirmed : valid(noticed) ? noticed : null
  if (!start) return { date: null, basis: "none", earlierThanLegalClock: false, note: "Enter when you first noticed the incident to see a date." }
  const basis = start === confirmed ? "confirmed" : "noticed"
  const earlier = !discoveryLike && basis === "noticed"
  return {
    date: addCalc(start, o.calc),
    basis,
    earlierThanLegalClock: earlier,
    note: earlier
      ? "Counted from when you first noticed it, which is the earliest this could be due. The legal clock may start later, when you determine or reasonably believe it qualifies. Ask counsel."
      : o.calc.unit === "business-days" ? "Counts weekdays only and ignores federal holidays, so the legal date may be later." : "",
  }
}

export function describeRemaining(due: Date, now = new Date()): { text: string; overdue: boolean } {
  const ms = due.getTime() - now.getTime()
  const overdue = ms < 0
  const abs = Math.abs(ms)
  const hours = Math.floor(abs / HOUR)
  const days = Math.floor(hours / 24)
  const text = hours < 48 ? `${hours} hour${hours === 1 ? "" : "s"}` : `${days} day${days === 1 ? "" : "s"}`
  return { text: overdue ? `${text} past` : `${text} left`, overdue }
}

export type ClockSource = "lawyer-due" | "lawyer-start" | "computed" | "none"
/**
 * running: has a date and counting. waiting: needs a fact we don't have yet (see waitingOn).
 * not-triggered: your answers suggest this rule isn't triggered (confirm with counsel).
 * paused: you paused it. no-date: depends on a decision or isn't in force.
 */
export type ClockState = "running" | "waiting" | "not-triggered" | "paused" | "no-date"

export interface ResolvedClock extends DueDate {
  obligation: Obligation
  source: ClockSource
  state: ClockState
  hidden: boolean
  override?: ClockOverride
  /** Questions that must be answered before this clock can start. */
  waitingOn: QuestionId[]
  /** Total time this clock has been paused, in milliseconds (including a pause in progress). */
  pausedMs: number
}

const validDate = (iso?: string) => {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

export function totalPausedMs(override: ClockOverride | undefined, now: Date): { ms: number; open: boolean } {
  let ms = 0
  let open = false
  for (const p of override?.pauses ?? []) {
    const from = validDate(p.fromIso)
    if (!from) continue
    const to = validDate(p.toIso)
    if (to) ms += Math.max(0, to.getTime() - from.getTime())
    else { ms += Math.max(0, now.getTime() - from.getTime()); open = true }
  }
  return { ms, open }
}

/** Checks the data-breach gate. Returns null when the clock may run. */
function gateStatus(i: Intake): { state: "waiting" | "not-triggered"; waitingOn: QuestionId[]; note: string } | null {
  // A lost device that was merely misplaced can still be a breach if it held unencrypted data, so
  // "nobody got in" never switches its clocks off unless the device was encrypted.
  const misplacedDevice = i.incidentType === "lost-device" && i.encrypted !== "yes"
  if (i.incidentType === "lost-device" && i.encrypted === "yes" && i.unauthorizedAccess === "no" && i.dataTaken !== "yes") {
    return { state: "not-triggered", waitingOn: [], note: "You said the device was misplaced and that it was encrypted and locked. This rule may not be triggered, but confirm with your lawyer before relying on that." }
  }
  // If they also said information WAS taken, "nobody got in" contradicts it: never switch a clock off on a contradiction.
  if (i.unauthorizedAccess === "no" && i.dataTaken !== "yes") {
    if (misplacedDevice) {
      // handled below: keep running with a caveat
    } else {
      return { state: "not-triggered", waitingOn: [], note: "You said no one got in without permission, so this rule probably isn't triggered. Confirm with your lawyer before relying on that." }
    }
  }

  const waitingOn: QuestionId[] = []
  if (i.unauthorizedAccess === "unknown" && !misplacedDevice) waitingOn.push("unauthorizedAccess")
  if (i.dataTaken === "unknown") waitingOn.push("dataTaken")
  if (waitingOn.length === 0) return null
  return {
    state: "waiting",
    waitingOn,
    note: "Not started yet: it begins once you know whether someone got in and whether information was taken. This does not give you extra time: some rules count what you reasonably should have known. Keep investigating and ask your lawyer.",
  }
}

/**
 * Resolve one obligation's clock, applying the user's adjustments (from counsel). Precedence:
 * an explicit due date from counsel wins; then a counsel-confirmed start time recomputed with the
 * rule's own duration; otherwise our conservative computed date. Data-breach clocks are gated on
 * what we know (see gateStatus) unless counsel has set a start or due date. Pauses slide the due
 * date forward so the remaining time stays frozen while paused.
 */
export function resolveClock(
  o: Obligation,
  noticedIso: string,
  confirmedIso: string,
  override?: ClockOverride,
  intake?: Intake,
  now: Date = new Date(),
): ResolvedClock {
  const hidden = !!override?.hidden
  const dueSet = validDate(override?.dueIso)
  const startSet = validDate(override?.startIso)
  const paused = totalPausedMs(override, now)
  const slide = (d: Date | null) => (d ? new Date(d.getTime() + paused.ms) : null)
  const common = { obligation: o, hidden, override, pausedMs: paused.ms, waitingOn: [] as QuestionId[] }

  if (dueSet) {
    return { ...common, source: "lawyer-due", state: paused.open ? "paused" : "running", date: slide(dueSet), basis: "none", earlierThanLegalClock: false, note: "Due date set by you, for example per your lawyer. We are not checking it." }
  }
  if (startSet && o.calc) {
    return { ...common, source: "lawyer-start", state: paused.open ? "paused" : "running", date: slide(addCalc(startSet, o.calc)), basis: "none", earlierThanLegalClock: false, note: "Counted from the start time you set, for example per your lawyer, using this rule's duration." }
  }
  if (o.gate === "data-acquisition" && intake) {
    const g = gateStatus(intake)
    if (g) return { ...common, source: "none", state: g.state, waitingOn: g.waitingOn, date: null, basis: "none", earlierThanLegalClock: false, note: g.note }
  }
  const computed = dueDate(o, noticedIso, confirmedIso)
  const date = slide(computed.date)
  const misplaced = o.gate === "data-acquisition" && intake?.incidentType === "lost-device" && intake.unauthorizedAccess !== "yes" && intake.encrypted !== "yes"
  const caveat = misplaced
    ? " This is a lost device. If it wasn't encrypted and locked, losing it can still count as a breach under some rules even if nobody is known to have looked at it, so this clock is running. Confirm with your lawyer."
    : o.gate === "data-acquisition" && intake?.dataTaken === "no"
      ? " You said no information was taken. Some rules still treat unauthorized access as a possible breach, so this clock is running. Confirm with your lawyer."
      : ""
  return {
    ...common,
    ...computed,
    date,
    note: (computed.note + caveat).trim(),
    source: computed.date ? "computed" : "none",
    state: !computed.date ? "no-date" : paused.open ? "paused" : "running",
  }
}

export function resolveAllClocks(
  intake: Intake,
  overrides: Record<string, ClockOverride>,
  now: Date = new Date(),
): ResolvedClock[] {
  return applicableObligations(intake).map((o) => resolveClock(o, intake.noticedAt, intake.confirmedAt, overrides[o.id], intake, now))
}

/** Percent of the window elapsed (0-100), or null when there is no start/end to measure. */
export function percentElapsed(start: Date | null, due: Date | null, now = new Date()): number | null {
  if (!start || !due) return null
  const total = due.getTime() - start.getTime()
  if (total <= 0) return 100
  return Math.min(100, Math.max(0, ((now.getTime() - start.getTime()) / total) * 100))
}
